import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import BottomTabNavigation from '../../components/navigation/BottomTabNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AlertConfigurationTab from './components/AlertConfigurationTab';
import DeliverySettingsTab from './components/DeliverySettingsTab';
import NotificationHistoryTab from './components/NotificationHistoryTab';
import notificationService from '../../services/notificationService';
import { useAuth } from '../../contexts/AuthContext';
import { logger } from '../../utils/logger';
import LogoutButton from '../../components/LogoutButton';
import CloseButton from '../../components/CloseButton';




const NotificationManagement = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('configuration');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({
    // Alert Configuration
    actionTypes: {
      dataModifications: true,
      accessAttempts: true,
      securityViolations: true,
      systemEvents: false
    },
    severityThresholds: {
      dataModifications: 5,
      accessAttempts: 7,
      securityViolations: 10,
      systemEvents: 3
    },
    // Role-based routing
    roleBasedRouting: {
      admin: true,
      healthcareProfessional: false,
      caregiver: true,
      standardUser: false
    },
    escalationEnabled: true,
    escalationThreshold: 8,
    // Delivery Settings
    deliveryMethods: {
      email: true,
      inApp: true,
      sms: false
    },
    quietHours: {
      enabled: false,
      start: '22:00',
      end: '07:00',
      timezone: Intl.DateTimeFormat()?.resolvedOptions()?.timeZone
    },
    batching: {
      enabled: true,
      interval: 'hourly',
      minAlerts: 3
    },
    emergencyOverride: true
  });

  useEffect(() => {
    if (user?.id) {
      loadNotificationSettings();
    }
  }, [user]);

  const loadNotificationSettings = async () => {
    try {
      setLoading(true);
      const settings = await notificationService?.getUserSettings(user?.id);
      
      if (settings?.notificationPreferences) {
        setNotificationSettings(prev => ({
          ...prev,
          ...settings?.notificationPreferences
        }));
      }
    } catch (error) {
      logger?.error('Failed to load notification settings', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await notificationService?.updateUserSettings(user?.id, {
        notificationPreferences: notificationSettings
      });
      logger?.info('Notification settings saved successfully');
    } catch (error) {
      logger?.error('Failed to save notification settings', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSettingChange = (category, key, value) => {
    setNotificationSettings(prev => ({
      ...prev,
      [category]: {
        ...prev?.[category],
        [key]: value
      }
    }));
  };

  const handleRootSettingChange = (key, value) => {
    setNotificationSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const tabs = [
    { id: 'configuration', label: 'Alert Configuration', icon: 'Settings' },
    { id: 'delivery', label: 'Delivery Settings', icon: 'Send' },
    { id: 'history', label: 'Notification History', icon: 'Clock' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'configuration':
        return (
          <AlertConfigurationTab
            settings={notificationSettings}
            onSettingChange={handleSettingChange}
            onRootSettingChange={handleRootSettingChange}
            loading={loading}
          />
        );
      case 'delivery':
        return (
          <DeliverySettingsTab
            settings={notificationSettings}
            onSettingChange={handleSettingChange}
            onRootSettingChange={handleRootSettingChange}
            loading={loading}
          />
        );
      case 'history':
        return <NotificationHistoryTab userId={user?.id} />;
      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>Notification Management - MyBodyToday</title>
        <meta name="description" content="Manage real-time audit alert preferences and notification delivery settings" />
      </Helmet>
      <div className="min-h-screen bg-background pb-20">
        <header className="sticky top-0 z-50 bg-card border-b border-border shadow-therapeutic">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
            <div className="flex items-center justify-between gap-3 sm:gap-4">
              <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                <button
                  onClick={() => navigate(-1)}
                  className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-muted transition-smooth"
                  aria-label="Go back"
                >
                  <Icon name="ChevronLeft" size={20} strokeWidth={2} />
                </button>
                <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-primary/10">
                  <Icon name="Bell" size={24} color="var(--color-primary)" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-xl sm:text-2xl md:text-3xl font-heading font-bold text-foreground">
                    Notification Management
                  </h1>
                  <p className="text-sm sm:text-base text-muted-foreground mt-1">
                    Control real-time audit alert preferences
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <LogoutButton showLabel={false} ariaLabel="Sign out from notification management" />
                <CloseButton
                  onClose={() => navigate('/settings-and-privacy')}
                  navigateTo="/settings-and-privacy"
                  ariaLabel="Close notification management"
                />
                {activeTab !== 'history' && (
                  <Button
                    onClick={handleSaveSettings}
                    loading={saving}
                    disabled={loading}
                    iconName="Save"
                    size="sm"
                    className="flex-shrink-0"
                  >
                    Save
                  </Button>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Tab Navigation */}
        <div className="sticky top-[88px] z-40 bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex gap-1 overflow-x-auto scrollbar-hide">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`
                    flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap
                    border-b-2 transition-smooth
                    ${activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                    }
                  `}
                >
                  <Icon name={tab?.icon} size={16} strokeWidth={2} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {renderTabContent()}
        </main>
      </div>
      <BottomTabNavigation />
    </>
  );
};

export default NotificationManagement;